/**
 * src/components/teacher-dashboard-components/theme/themeConfig.js
 *
 * React Component Module.
 *
 * Purpose: React UI module containing one or more components and local UI helpers.
 *
 * Data flow (high level):
 * - UI/Caller -> this module -> downstream helpers/services -> state/response.
 */
export const themeConfig = {
  colors: {
    primary: {
      blue: '#113F67',
      lightBlue: '#34699A',
      skyBlue: '#58A0C8'
    },
    accent: {
      // Semantic accent colors used via getColor('accent.*')
      // Keep these high-contrast for text/CTA usage.
      main: '#58A0C8',
      light: '#E0F2FE',
      dark: '#113F67',
      yellow: '#FDF5AA',
      lightYellow: '#FEFCDD'
    },
    neutral: {
      white: '#FFFFFF',
      light: '#F8FAFC',
      medium: '#E2E8F0',
      dark: '#475569',
      black: '#1E293B'
    },
    semantic: {
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6'
    },
    gradients: {
      primary: 'linear-gradient(135deg, #113F67 0%, #34699A 50%, #58A0C8 100%)',
      soft: 'linear-gradient(135deg, #58A0C8 0%, #FDF5AA 100%)',
      card: 'linear-gradient(145deg, #FFFFFF 0%, #F8FAFC 100%)'
    }
  },
  
  typography: {
    fontFamily: {
      primary: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      secondary: '"Poppins", sans-serif',
      mono: '"JetBrains Mono", "Fira Code", monospace'
    },
    
    desktop: {
      h1: {
        fontSize: '2.5rem',
        fontWeight: '700',
        lineHeight: '1.2',
        letterSpacing: '-0.025em'
      },
      h2: {
        fontSize: '2rem',
        fontWeight: '600',
        lineHeight: '1.3',
        letterSpacing: '-0.025em'
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: '600',
        lineHeight: '1.4'
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: '500',
        lineHeight: '1.4'
      },
      body: {
        fontSize: '1rem',
        fontWeight: '400',
        lineHeight: '1.6'
      },
      small: {
        fontSize: '0.875rem',
        fontWeight: '400',
        lineHeight: '1.5'
      },
      caption: {
        fontSize: '0.75rem',
        fontWeight: '400',
        lineHeight: '1.4'
      }
    },
    
    mobile: {
      h1: {
        fontSize: '1.875rem',
        fontWeight: '600',
        lineHeight: '1.3',
        letterSpacing: '-0.025em'
      },
      h2: {
        fontSize: '1.5rem',
        fontWeight: '600',
        lineHeight: '1.3',
        letterSpacing: '-0.025em'
      },
      h3: {
        fontSize: '1.25rem',
        fontWeight: '500',
        lineHeight: '1.4'
      },
      h4: {
        fontSize: '1.125rem',
        fontWeight: '500',
        lineHeight: '1.4'
      },
      body: {
        fontSize: '0.875rem',
        fontWeight: '400',
        lineHeight: '1.5'
      },
      small: {
        fontSize: '0.75rem',
        fontWeight: '300',
        lineHeight: '1.4'
      },
      caption: {
        fontSize: '0.6875rem',
        fontWeight: '300',
        lineHeight: '1.3'
      }
    }
  },
  
  spacing: {
    xs: '0.25rem',
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem',
    '2xl': '3rem',
    '3xl': '4rem'
  },
  
  borderRadius: {
    sm: '0.375rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px'
  },
  
  shadows: {
    sm: '0 1px 2px 0 rgba(17, 63, 103, 0.05)',
    md: '0 4px 6px -1px rgba(17, 63, 103, 0.1), 0 2px 4px -1px rgba(17, 63, 103, 0.06)',
    lg: '0 10px 15px -3px rgba(17, 63, 103, 0.1), 0 4px 6px -2px rgba(17, 63, 103, 0.05)',
    xl: '0 20px 25px -5px rgba(17, 63, 103, 0.1), 0 10px 10px -5px rgba(17, 63, 103, 0.04)',
    inner: 'inset 0 2px 4px 0 rgba(17, 63, 103, 0.06)'
  },
  
  animations: {
    duration: {
      fast: '150ms',
      normal: '300ms',
      slow: '500ms'
    },
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)'
    }
  },
  
  breakpoints: {
    xs: '320px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
    '2xl': '1400px'
  },
  
  components: {
    button: {
      primary: {
        background: '#113F67',
        color: '#FFFFFF',
        hover: '#34699A',
        active: '#0F3559'
      },
      secondary: {
        background: '#58A0C8',
        color: '#FFFFFF',
        hover: '#4A8BB8',
        active: '#3F79A3'
      },
      outline: {
        background: 'transparent',
        color: '#113F67',
        border: '#113F67',
        hover: '#113F67',
        hoverColor: '#FFFFFF'
      },
      ghost: {
        background: 'transparent',
        color: '#113F67',
        hover: 'rgba(17, 63, 103, 0.1)'
      }
    },
    
    card: {
      background: '#FFFFFF',
      border: '#E2E8F0',
      shadow: '0 4px 6px -1px rgba(17, 63, 103, 0.1)'
    },
    
    input: {
      background: '#FFFFFF',
      border: '#E2E8F0',
      focus: '#58A0C8',
      error: '#EF4444'
    }
  }
};
